import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/shop", label: "SHOP" },
  { to: "/market", label: "RESALE" },
  { to: "/events", label: "EVENTS" },
  { to: "/gallery", label: "GALLERY" },
  { to: "/find-us", label: "FIND US" },
];

export const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-black/70 backdrop-blur-xl" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:px-8">
        <Link to="/" className="font-display text-lg font-extrabold tracking-tighter" data-testid="logo-link">
          THE HOODIE PROJECT<span className="text-muted-foreground">®</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-xs tracking-[0.15em] text-muted-foreground transition-colors hover:text-white">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {user ? (
            <div className="hidden items-center gap-3 md:flex">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-xs tracking-[0.15em] text-white hover:underline">ADMIN</Link>
              )}
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
                className="text-xs tracking-[0.15em] text-muted-foreground hover:text-white">LOGOUT</button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:block">
              <User className="h-5 w-5 text-muted-foreground transition-colors hover:text-white" />
            </Link>
          )}

          <Link to="/cart" data-testid="cart-link" className="relative">
            <ShoppingBag className="h-5 w-5 text-white" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center bg-white px-1 text-[10px] font-bold text-black">
                {count}
              </span>
            )}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <button data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-border bg-black">
              <div className="mt-8 flex flex-col gap-6">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    className="font-display text-2xl font-bold tracking-tight hover:text-muted-foreground">
                    {l.label}
                  </Link>
                ))}
                <div className="h-px bg-border" />
                {user ? (
                  <>
                    {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm tracking-widest">ADMIN</Link>}
                    <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left text-sm tracking-widest text-muted-foreground">LOGOUT</button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-widest">LOGIN / SIGN UP</Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
